import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import type { ModalOwnProps } from '@ori-ui/components';
import { BurgerMenu as BurgerMenuIcon } from '@ori-ui/icons';
import { Backdrop, Box, CircularProgress, Slide } from '@ori-ui/mui';
import { useToken } from '@ori/auth';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useMemo, lazy, Suspense, useCallback } from 'react';

import { useActiveMenu } from '../../context/navigation';
import { useIsMobile, useCustomerInitials, useLazyPersonalMenu } from '../../hooks';
import { useTranslations } from '../../hooks/useTranslations';
import { BurgerMenuTabNames } from '../../models';
import { BurgerMenuContent } from './BurgerMenuContent';
import { BurgerMenuTab } from './BurgerMenuTab';
import { LazyLinks } from './Links/LazyLinks';
import { BURGER_MENU_BACKDROP, BURGER_MENU_CONTAINER, BURGER_MENU_ROOT } from './constants';
import { BurgerMenuContainer, BurgerMenuInner, BurgerMenuRoot, ModalRoot } from './styles';

const LazyBurgerMenuLinks = lazy(async () => import('./Links/LazyLinks')) as FC;

const fallback = (
  <Box
    display="flex"
    justifyContent="center"
  >
    <CircularProgress />
  </Box>
);

export const BurgerMenu: FC = () => {
  const { BurgerMenu: BurgerMenuType, AccountSideMenu: AccountSideMenuType } = ToggleMenuFlyout.MenuTypes;
  const { activeMenu, toggleActiveMenu } = useActiveMenu();
  const isMobile = useIsMobile();
  const ownerState = { isMobile };
  const handleTabClick = useCallback(
    (tabName: BurgerMenuTabNames) => {
      const menuType: ToggleMenuFlyout.MenuTypes =
        tabName === BurgerMenuTabNames.ProductsTab ? BurgerMenuType : AccountSideMenuType;
      toggleActiveMenu(menuType);
    },
    [AccountSideMenuType, BurgerMenuType, toggleActiveMenu],
  );
  const { translations } = useTranslations();
  const customerInitials = useCustomerInitials();
  const { isAnonymousUser } = useToken();
  const isLoggedIn = !isAnonymousUser;
  const isMenuActive = activeMenu === BurgerMenuType || (isMobile && activeMenu === AccountSideMenuType);
  const activeTab = activeMenu === BurgerMenuType ? BurgerMenuTabNames.ProductsTab : BurgerMenuTabNames.AccountTab;
  const { getTestId } = useTestId();
  const personalMenu = useLazyPersonalMenu();

  const onClickOverlay = useCallback(() => {
    toggleActiveMenu(BurgerMenuType);
  }, [BurgerMenuType, toggleActiveMenu]);

  const slots: ModalOwnProps['slots'] = useMemo(
    () => ({
      backdrop: Backdrop,
    }),
    [],
  );

  if (isMobile) {
    return (
      <ModalRoot
        slots={slots}
        open={isMenuActive}
        onClose={onClickOverlay}
      >
        <Slide
          in={isMenuActive}
          direction="right"
        >
          <BurgerMenuRoot
            ownerState={ownerState}
            data-testid={getTestId(BURGER_MENU_ROOT)}
          >
            <BurgerMenuContainer data-testid={getTestId(BURGER_MENU_CONTAINER)}>
              <BurgerMenuTab
                activeTab={activeTab}
                tabName={BurgerMenuTabNames.ProductsTab}
                tabPrefix={BurgerMenuIcon}
                onTabClick={handleTabClick}
              >
                {translations.menu}
              </BurgerMenuTab>
              {isLoggedIn ? (
                <BurgerMenuTab
                  activeTab={activeTab}
                  tabName={BurgerMenuTabNames.AccountTab}
                  tabPrefix={customerInitials ?? ''}
                  isLoggedIn={isLoggedIn}
                  onTabClick={handleTabClick}
                >
                  {translations.myAccount}
                </BurgerMenuTab>
              ) : null}
            </BurgerMenuContainer>
            <Suspense fallback={fallback}>
              {activeTab === BurgerMenuTabNames.ProductsTab && <LazyBurgerMenuLinks />}
              {activeTab === BurgerMenuTabNames.AccountTab && !isAnonymousUser ? personalMenu : null}
            </Suspense>
          </BurgerMenuRoot>
        </Slide>
      </ModalRoot>
    );
  }

  if (!isMenuActive) {
    return null;
  }

  return (
    <BurgerMenuRoot
      as="div"
      ownerState={{ ...ownerState, isDesktop: true }}
      data-testid={getTestId(BURGER_MENU_ROOT)}
    >
      <BurgerMenuInner>
        <LazyLinks />
        <BurgerMenuContent />
        <Backdrop
          open
          data-testid={getTestId(BURGER_MENU_BACKDROP)}
          sx={{ position: 'absolute', zIndex: '-1', width: '100vw', height: '100vh' }}
          onClick={onClickOverlay}
        />
      </BurgerMenuInner>
    </BurgerMenuRoot>
  );
};
